import { defineStore } from 'pinia'
import { ref } from 'vue'
import { genAiMindmap, getDetailMinmap } from '@/api/mindmap'

export const useMindmapStore = defineStore('mindmap', () => {
  // State
  const isLoading = ref(false)
  const mindMapData = ref<any>(null)
  const mindmapId = ref('')
  const conversationId = ref('')
  const currentQuery = ref('')
  const currentWorkspaceId = ref('')
  const isFromQuickTool = ref(false)

  // Helper function to transform API response to MindMap format
  const transformApiDataToMindMap = (apiData: any) => {
    // Handle case where apiData is null or undefined
    if (!apiData)
      return null

    const addMissingProperties = (node: any): any => {
      if (!node)
        return null

      const transformedNode = {
        data: {
          text: node.data?.text || '',
          richText: node.data?.richText || false,
          expand: node.data?.expand ?? true,
          isActive: node.data?.isActive || false,
          uid: node.data?.uid || '',
          icon: node.data?.icon || [],
          image: node.data?.image || '',
          imageTitle: node.data?.imageTitle || '',
          imageSize: node.data?.imageSize || {
            width: 100,
            height: 100,
            custom: false,
          },
          shape: node.data?.shape || 'rectangle',
          hyperlink: node.data?.hyperlink || '',
          hyperlinkTitle: node.data?.hyperlinkTitle || '',
          note: node.data?.note || '',
          tag: node.data?.tag || [],
          generalization: node.data?.generalization || null,
          associativeLineTargets: node.data?.associativeLineTargets || [],
          associativeLineText: node.data?.associativeLineText || {},
          associativeLinePoint: node.data?.associativeLinePoint || [],
          associativeLineTargetControlOffsets: node.data?.associativeLineTargetControlOffsets || [],
          associativeLineStyle: node.data?.associativeLineStyle || {},
          customLeft: node.data?.customLeft || 0,
          customTop: node.data?.customTop || 0,
          customTextWidth: node.data?.customTextWidth || 0,
          dir: node.data?.dir || '',
          // Font and text styling properties
          fontFamily: node.data?.fontFamily,
          fontSize: node.data?.fontSize,
          lineHeight: node.data?.lineHeight,
          color: node.data?.color,
          fontWeight: node.data?.fontWeight,
          textDecoration: node.data?.textDecoration,
          // Border and shape properties
          borderColor: node.data?.borderColor,
          borderDasharray: node.data?.borderDasharray,
          borderWidth: node.data?.borderWidth,
          borderRadius: node.data?.borderRadius,
          fillColor: node.data?.fillColor,
          // Padding properties
          paddingX: node.data?.paddingX,
          paddingY: node.data?.paddingY,
        },
        children: [],
      }

      if (node.children && Array.isArray(node.children))
        transformedNode.children = node.children.map(addMissingProperties).filter(Boolean)

      return transformedNode
    }

    return {
      layout: apiData.layout || 'logicalStructure',
      root: addMissingProperties(apiData.root ? apiData.root : apiData),
      theme: {
        template: apiData.theme?.template || 'default',
        config: apiData.theme?.config || {},
      },
      view: apiData.view || {
        transform: {
          scaleX: 1,
          scaleY: 1,
          shear: 0,
          rotate: 0,
          translateX: 0,
          translateY: 0,
          originX: 0,
          originY: 0,
          a: 1,
          b: 0,
          c: 0,
          d: 1,
          e: 0,
          f: 0,
        },
        state: {
          scale: 1,
          x: 0,
          y: 0,
          sx: 0,
          sy: 0,
        },
      },
    }
  }

  // Actions
  const generateMindmap = async (query: string, workspaceId: string, conversationIdParam?: string) => {
    try {
      isLoading.value = true
      currentQuery.value = query
      currentWorkspaceId.value = workspaceId

      const requestData = {
        query,
        workspace_id: workspaceId,
        conversation_id: conversationIdParam || undefined,
      }

      const response = await genAiMindmap(requestData)

      if (response.success && response.data) {
        conversationId.value = response.data.conversation_id
        mindmapId.value = response.data.id
        const transformedData = transformApiDataToMindMap(response.data.mindmap_data)
        mindMapData.value = transformedData
        return {
          success: true,
          message: response.message || 'Tạo sơ đồ tư duy thành công!',
          conversationId: response.data.conversation_id,
          mindmapId: response.data.id,
        }
      }
      else {
        return { success: false, message: response.message || 'Có lỗi xảy ra khi tạo sơ đồ tư duy' }
      }
    }
    catch (error) {
      console.error('Error generating mindmap:', error)
      return { success: false, message: 'Có lỗi xảy ra khi tạo sơ đồ tư duy' }
    }
    finally {
      isLoading.value = false
    }
  }

  const clearMindmap = () => {
    isLoading.value = false
    mindMapData.value = null
    mindmapId.value = ''
    conversationId.value = ''
    currentQuery.value = ''
    currentWorkspaceId.value = ''
    isFromQuickTool.value = false
  }

  const hasMindmapData = () => {
    return !!mindMapData.value
  }

  const loadMindmapById = async (mindmapIdParam: string) => {
    try {
      isLoading.value = true

      const response = await getDetailMinmap(mindmapIdParam)

      if (response.success && response.data) {
        conversationId.value = response.data.conversation_id
        mindmapId.value = response.data.id

        // Check if mindmap_data exists
        if (!response.data.mindmap_data)
          return { success: false, message: 'Dữ liệu sơ đồ tư duy không hợp lệ' }

        const transformedData = transformApiDataToMindMap(response.data.mindmap_data)

        // Check if transformation was successful
        if (!transformedData)
          return { success: false, message: 'Không thể xử lý dữ liệu sơ đồ tư duy' }

        mindMapData.value = transformedData
        return { success: true, message: 'Tải sơ đồ tư duy thành công!' }
      }
      else {
        return { success: false, message: response.message || 'Không thể tải sơ đồ tư duy' }
      }
    }
    catch (error) {
      console.error('Error loading mindmap:', error)
      return { success: false, message: 'Có lỗi xảy ra khi tải sơ đồ tư duy' }
    }
    finally {
      isLoading.value = false
    }
  }

  return {
    // State
    isLoading,
    mindMapData,
    mindmapId,
    conversationId,
    currentQuery,
    currentWorkspaceId,
    isFromQuickTool,

    // Actions
    generateMindmap,
    loadMindmapById,
    clearMindmap,
    hasMindmapData,
  }
})
